import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Search, X, ChevronDown } from 'lucide-react';

/**
 * SearchableSelect - Componente de busca com dropdown navegável
 * Usa position:fixed para escapar do overflow do modal sem usar portal
 */
export function SearchableSelect({
  options = [],
  value,
  onChange,
  placeholder = "Selecione...",
  searchPlaceholder = "Digite para buscar...",
  filterFn,
  disabled = false,
  emptyMessage = "Nenhum resultado encontrado",
  prependOption,
  className = "",
  showSelectedLabel = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [dropdownStyle, setDropdownStyle] = useState({});

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const dropdownRef = useRef(null);
  const optionRefs = useRef([]);

  const normalizeText = (text) => {
    if (!text) return '';
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[.\-\/\s]+/g, '');
  };

  const filteredOptions = search
    ? (filterFn
        ? filterFn(options, search)
        : options.filter(opt => {
            const normalizedSearch = normalizeText(search);
            const normalizedLabel = normalizeText(opt.label);
            const normalizedSublabel = normalizeText(opt.sublabel);
            const originalSearch = search.toLowerCase();
            const originalLabel = (opt.label || '').toLowerCase();
            const originalSublabel = (opt.sublabel || '').toLowerCase();

            return normalizedLabel.includes(normalizedSearch) ||
                   normalizedSublabel.includes(normalizedSearch) ||
                   originalLabel.includes(originalSearch) ||
                   originalSublabel.includes(originalSearch);
          })
      )
    : options;

  const totalOptions = (prependOption ? 1 : 0) + filteredOptions.length;
  const selectedOption = options.find(opt => opt.id === value);
  const selectedLabel = selectedOption?.label || (prependOption?.id === value ? prependOption.label : '');

  // Calcular posição fixed do dropdown
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const maxH = Math.min(380, window.innerHeight * 0.45);

    // Inside a transformed ancestor (e.g. Dialog), fixed breaks: anchor to the trigger instead
    if (triggerRef.current.closest('[role="dialog"]')) {
      const dialogRect = triggerRef.current.closest('[role="dialog"]').getBoundingClientRect();
      const below = dialogRect.bottom - rect.bottom;
      const above = rect.top - dialogRect.top;
      const openUp = below < 220 && above > below;
      setDropdownStyle({
        position: 'absolute',
        left: 0,
        right: 0,
        ...(openUp ? { bottom: '100%', marginBottom: 2 } : { top: '100%', marginTop: 2 }),
        maxHeight: Math.max(200, Math.min(maxH, (openUp ? above : below) - 10)),
        zIndex: 1000,
      });
      return;
    }

    if (spaceBelow < maxH && rect.top > spaceBelow) {
      setDropdownStyle({
        position: 'fixed',
        bottom: window.innerHeight - rect.top + 2,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxH, rect.top - 10),
        zIndex: 99999,
      });
    } else {
      setDropdownStyle({
        position: 'fixed',
        top: rect.bottom + 2,
        left: rect.left,
        width: rect.width,
        maxHeight: Math.min(maxH, spaceBelow - 10),
        zIndex: 99999,
      });
    }
  }, []);

  // Fechar ao clicar fora
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e) => {
      if (containerRef.current?.contains(e.target)) return;
      if (dropdownRef.current?.contains(e.target)) return;
      setIsOpen(false);
      setSearch('');
    };
    // Use capture phase to be first
    document.addEventListener('mousedown', handleClickOutside, true);
    return () => document.removeEventListener('mousedown', handleClickOutside, true);
  }, [isOpen]);

  // Scroll para o item destacado
  useEffect(() => {
    if (isOpen && optionRefs.current[highlightedIndex]) {
      optionRefs.current[highlightedIndex].scrollIntoView({
        block: 'nearest',
        behavior: 'smooth'
      });
    }
  }, [highlightedIndex, isOpen]);

  useEffect(() => { setHighlightedIndex(0); }, [search]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [isOpen, updatePosition]);

  const handleSelect = (optionId) => {
    onChange(optionId);
    setIsOpen(false);
    setSearch('');
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => prev < totalOptions - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : totalOptions - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (totalOptions > 0) {
          if (prependOption && highlightedIndex === 0) {
            handleSelect(prependOption.id);
          } else {
            const idx = prependOption ? highlightedIndex - 1 : highlightedIndex;
            if (filteredOptions[idx]) handleSelect(filteredOptions[idx].id);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearch('');
        break;
      case 'Tab':
        setIsOpen(false);
        setSearch('');
        break;
      default:
        break;
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearch('');
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Trigger */}
      <div
        ref={triggerRef}
        className={`
          w-full px-3 py-2 border rounded-md text-sm flex items-center justify-between cursor-pointer
          ${disabled ? 'bg-slate-100 cursor-not-allowed' : 'bg-white hover:border-slate-400'}
          ${isOpen ? 'border-blue-500 ring-2 ring-blue-200' : 'border-slate-300'}
        `}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        {value && showSelectedLabel ? (
          <div className="flex items-center justify-between flex-1">
            <span className="text-slate-800">{selectedLabel}</span>
            <button type="button" onClick={handleClear} className="ml-2 text-slate-400 hover:text-slate-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <span className="text-slate-400">{placeholder}</span>
        )}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown - fixed position, inside React tree */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="bg-white border border-slate-300 rounded-md shadow-xl flex flex-col"
          style={dropdownStyle}
          onMouseDown={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {/* Busca */}
          <div className="p-2 border-b border-slate-200 flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                ref={inputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-blue-500"
                autoComplete="off"
              />
            </div>
            {search && (
              <div className="mt-1 text-xs text-slate-500 px-1">
                {filteredOptions.length} resultado(s)
              </div>
            )}
          </div>

          {/* Lista */}
          <ul
            ref={listRef}
            className="overflow-y-auto flex-1"
            role="listbox"
            style={{ scrollBehavior: 'smooth' }}
          >
            {prependOption && (
              <li
                ref={el => optionRefs.current[0] = el}
                className={`px-3 py-2.5 cursor-pointer flex items-center gap-2 border-b border-slate-100
                  ${highlightedIndex === 0 ? 'bg-blue-100 text-blue-900' : 'hover:bg-slate-50'}`}
                onClick={() => handleSelect(prependOption.id)}
                onMouseEnter={() => setHighlightedIndex(0)}
                role="option"
                aria-selected={highlightedIndex === 0}
              >
                {prependOption.icon && <span className="text-lg">{prependOption.icon}</span>}
                <div>
                  <div className="font-medium">{prependOption.label}</div>
                  {prependOption.sublabel && <div className="text-xs text-slate-500">{prependOption.sublabel}</div>}
                </div>
              </li>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => {
                const actualIndex = prependOption ? index + 1 : index;
                return (
                  <li
                    key={option.id}
                    ref={el => optionRefs.current[actualIndex] = el}
                    className={`px-3 py-2.5 cursor-pointer border-b border-slate-100 last:border-b-0
                      ${highlightedIndex === actualIndex ? 'bg-blue-100 text-blue-900' : 'hover:bg-slate-50'}`}
                    onClick={() => handleSelect(option.id)}
                    onMouseEnter={() => setHighlightedIndex(actualIndex)}
                    role="option"
                    aria-selected={highlightedIndex === actualIndex}
                  >
                    <div className="font-medium text-slate-800">{option.label}</div>
                    {option.sublabel && <div className="text-xs text-slate-500">{option.sublabel}</div>}
                  </li>
                );
              })
            ) : (
              <li className="px-3 py-4 text-center text-slate-500">
                <span className="text-amber-600">{emptyMessage}</span>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableSelect;
